import { useState } from "react";
import { Box, Button, IconButton, Stack, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RestoreIcon from "@mui/icons-material/Restore";
import { useLang, useT } from "src/i18n";
import { formatSentDate } from "src/utils/valut/valutFormatters";
import FrostedCard from "./FrostedCard";

// Slim, dismissible "you're continuing a previous request" banner shown ABOVE the
// wizard card. Replaces the old full-screen QuoteResumeBanner takeover: the draft
// is already hydrated and the user is on the right step, so this only *informs* and
// offers an escape. "Aloita alusta" is destructive (deletes the draft) so it gates
// behind an inline confirm rather than firing on the first click.
export default function QuoteResumeNotice({ address, at, onStartOver, onDismiss }) {
    const [confirming, setConfirming] = useState(false);
    const { lang } = useLang();
    const t = useT();
    // formatSentDate returns "—" for missing/unparseable input; the em dash isn't
    // a real date, so keep the "omit the meta line" behaviour formatWhen(null) had.
    const whenRaw = formatSentDate(at, lang);
    const when = whenRaw === "—" ? null : whenRaw;
    const meta = [address, when ? t("wizard.resumeNotice.startedAt", { when }) : null].filter(Boolean).join(" · ");

    const linkSx = {
        textTransform: "none",
        letterSpacing: 0,
        fontWeight: 500,
        minWidth: 0,
        px: 0.5,
        "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
    };

    return (
        <FrostedCard
            sx={{
                mb: 2,
                p: { xs: 1.5, sm: "12px 16px" },
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: { xs: 1, sm: 1.5 },
            }}
        >
            <RestoreIcon sx={{ fontSize: 20, color: "text.secondary", flexShrink: 0 }} aria-hidden />
            <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                <Box sx={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: "text.primary",
                    lineHeight: 1.35,
                }}>
                    {t("wizard.resumeNotice.title")}
                </Box>
                {meta && (
                    <Box sx={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 12.5,
                        color: "text.secondary",
                        lineHeight: 1.35,
                    }}>
                        {meta}
                    </Box>
                )}
            </Box>

            {confirming ? (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flexShrink: 0 }}>
                    <Box component="span" sx={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: 12.5,
                        color: "text.secondary",
                        mr: 0.5,
                    }}>
                        {t("wizard.resumeNotice.confirmPrompt")}
                    </Box>
                    <Button size="small" onClick={onStartOver} sx={{ ...linkSx, color: "error.main" }}>
                        {t("wizard.shared.startOver")}
                    </Button>
                    <Button size="small" onClick={() => setConfirming(false)} sx={{ ...linkSx, color: "text.secondary" }}>
                        {t("wizard.shared.cancel")}
                    </Button>
                </Stack>
            ) : (
                <Button
                    size="small"
                    onClick={() => setConfirming(true)}
                    sx={{ ...linkSx, color: "text.secondary", flexShrink: 0 }}
                >
                    {t("wizard.shared.startOver")}
                </Button>
            )}

            <Tooltip title={t("common.dialog.close")}>
                <IconButton
                    size="small"
                    onClick={onDismiss}
                    aria-label={t("wizard.resumeNotice.hideAriaLabel")}
                    sx={{
                        flexShrink: 0,
                        color: "text.secondary",
                        "&:hover": { color: "text.primary", backgroundColor: "rgba(244,242,238,0.08)" },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </FrostedCard>
    );
}
